/**
 * A member's face, with initials as the fallback.
 *
 * Deliberately never renders a broken-image box: an avatar that fails to load
 * falls back to the monogram rather than showing the browser's torn-page icon in
 * the middle of the directory.
 */
import { useState } from 'react';
import { apiUrl } from '../lib/api';

const SIZES = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-11 w-11 text-sm',
  lg: 'h-16 w-16 text-lg',
  xl: 'h-24 w-24 text-2xl',
} as const;

export function Avatar({
  firstName,
  lastName,
  avatarUrl,
  size = 'md',
  className = '',
}: {
  firstName: string;
  lastName?: string | null;
  avatarUrl?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);

  const initials =
    `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '·';

  const base = `${SIZES[size]} shrink-0 overflow-hidden rounded-full border border-gold-500/25 ${className}`;

  // The API hands back an API-prefixed path (`/api/v1/media/…`). `apiUrl` prepends
  // the whole base (which already ends in the prefix), so strip the prefix first
  // or you get `/api/v1/api/v1/media/…`.
  const src = avatarUrl
    ? avatarUrl.startsWith('http')
      ? avatarUrl
      : apiUrl(avatarUrl.replace(/^\/api\/v\d+/, ''))
    : null;

  if (!src || failed) {
    return (
      <span
        className={`${base} grid place-items-center bg-wine-900 font-display text-gold-200`}
        aria-hidden="true"
      >
        {initials}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={`${base} object-cover`}
    />
  );
}
